<template>
  <div class="space-y-4">
    <!-- What this rule actually does, in a sentence, updating as the controls
         below are touched. This is the single most important element here:
         without it the only way to know what "every 2, weeks, mon+thu, ends
         after 10" produces is to build the schedule in your head and hope. -->
    <p class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-[13px] leading-relaxed text-blue-900 dark:text-blue-200">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
      </svg>
      <span>{{ summary }}</span>
    </p>

    <!-- How often. Presets first, so the common cases are one click and
         nobody has to think in intervals to express "every week". -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">How often</label>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="preset in PRESETS"
          :key="preset.key"
          type="button"
          @click="applyPreset(preset)"
          class="px-3 py-1.5 text-[13px] font-medium rounded-full border transition-all duration-150 active:scale-95"
          :class="activePreset === preset.key
            ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
            : 'bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'"
        >{{ preset.label }}</button>
      </div>
    </div>

    <!-- The interval control only appears for Custom. For every preset it is
         already decided, and showing it anyway invites people to edit a
         number that silently contradicts the chip they just picked. -->
    <div v-if="activePreset === 'custom'" class="flex items-center gap-2">
      <span class="text-[13px] text-gray-500 dark:text-gray-400 flex-shrink-0">Repeat every</span>
      <input
        v-model.number="rule.interval"
        type="number" min="1" max="365"
        class="w-16 h-9 px-2 text-sm text-center text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <select
        v-model="rule.frequency"
        class="flex-1 min-w-0 h-9 px-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <!-- Plain plurals, not "day(s)". The number beside it already says
             which one is meant. -->
        <option value="daily">{{ rule.interval > 1 ? 'days' : 'day' }}</option>
        <option value="weekly">{{ rule.interval > 1 ? 'weeks' : 'week' }}</option>
        <option value="monthly">{{ rule.interval > 1 ? 'months' : 'month' }}</option>
      </select>
    </div>

    <!-- Weekly: which days -->
    <div v-if="rule.frequency === 'weekly'">
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">On these days</label>
      <div class="flex gap-1">
        <button
          v-for="day in DAYS"
          :key="day.key"
          type="button"
          @click="toggleDay(day.key)"
          :title="day.name"
          :aria-label="day.name"
          :aria-pressed="rule.days_of_week.includes(day.key)"
          class="flex-1 h-9 rounded-lg text-[11px] font-semibold transition-all duration-150 active:scale-95"
          :class="rule.days_of_week.includes(day.key)
            ? 'bg-blue-600 text-white shadow-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'"
        >{{ day.label }}</button>
      </div>
    </div>

    <!-- Monthly: which day of the month -->
    <div v-if="rule.frequency === 'monthly'">
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">On day of the month</label>
      <select
        v-model="dayOfMonthModel"
        class="w-full h-9 px-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option v-for="d in 31" :key="d" :value="String(d)">Day {{ d }}</option>
        <option value="last">Last day of the month</option>
      </select>
      <!-- Answers the obvious question before it is asked. The backend
           clamps to the month's real last day, so this is accurate. -->
      <p v-if="shortMonthWarning" class="mt-1.5 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
        Shorter months use their last day instead.
      </p>
    </div>

    <!-- Starts on. The app's DatePicker, not a native date input, so it
         shows the account's own date format like every other date field. -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Starts on</label>
      <DatePicker v-model="startsOnModel" placeholder="Pick a date" input-class="px-3 py-2 text-sm rounded-lg" />
    </div>

    <!-- Ends. One row per option, each a real click target, with its extra
         input revealed underneath rather than crammed onto the label line. -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ends</label>
      <div class="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
        <!-- The row is a div and only the radio line is a <label>. Wrapping
             the whole row would make every click inside the revealed control
             a label activation too, which steals focus from the date picker
             popover and the number input. -->
        <div
          v-for="option in END_OPTIONS"
          :key="option.value"
          class="px-3 py-2.5 transition-colors duration-150"
          :class="rule.ends.type === option.value
            ? 'bg-blue-50/70 dark:bg-blue-900/15'
            : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'"
        >
          <label class="flex items-center gap-2.5 cursor-pointer">
            <input type="radio" :value="option.value" v-model="rule.ends.type" class="text-blue-600 focus:ring-blue-500" />
            <span class="text-[13px] font-medium text-gray-700 dark:text-gray-200">{{ option.label }}</span>
          </label>

          <div v-if="rule.ends.type === 'on_date' && option.value === 'on_date'" class="mt-2 pl-[26px]">
            <DatePicker v-model="endsOnModel" placeholder="Pick a date" input-class="px-3 py-2 text-sm rounded-lg" />
          </div>

          <div v-if="rule.ends.type === 'after_count' && option.value === 'after_count'" class="mt-2 pl-[26px] flex items-center gap-2">
            <input
              v-model.number="rule.ends.count"
              type="number" min="1"
              class="w-20 h-9 px-2 text-sm text-center text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span class="text-[13px] text-gray-500 dark:text-gray-400">times</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Reminder last: it is a separate concern from the schedule, and
         sitting it mid form broke the start to end reading order. -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Remind me</label>
      <select
        v-model="remindBeforeModel"
        class="w-full h-9 px-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option v-for="opt in REMIND_OPTIONS" :key="String(opt.value)" :value="String(opt.value)">{{ opt.label }}</option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, reactive, ref } from 'vue'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import { useGlobalStore } from 'stores/global.js'
import { useFormatting } from 'composables/useFormatting.js'

const globalStore = useGlobalStore()
const { dateFormatMask, formatDateInMask } = useFormatting()

// Two letters, not one. The old single-letter row was S M T W T F S, where
// the two S's and two T's are indistinguishable without counting positions.
const DAYS = [
  { key: 'sun', label: 'Su', name: 'Sunday' },
  { key: 'mon', label: 'Mo', name: 'Monday' },
  { key: 'tue', label: 'Tu', name: 'Tuesday' },
  { key: 'wed', label: 'We', name: 'Wednesday' },
  { key: 'thu', label: 'Th', name: 'Thursday' },
  { key: 'fri', label: 'Fr', name: 'Friday' },
  { key: 'sat', label: 'Sa', name: 'Saturday' },
]
const DAY_KEYS = DAYS.map((d) => d.key)
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri']

// Named schedules people actually ask for, so the common cases are one
// click. Anything outside them falls through to Custom, which is the only
// place the raw "every N units" control appears.
const PRESETS = [
  { key: 'daily', label: 'Every day', frequency: 'daily', interval: 1 },
  { key: 'weekdays', label: 'Every weekday', frequency: 'weekly', interval: 1, days: WEEKDAY_KEYS },
  { key: 'weekly', label: 'Every week', frequency: 'weekly', interval: 1, days: 'start' },
  { key: 'biweekly', label: 'Every 2 weeks', frequency: 'weekly', interval: 2, days: 'start' },
  { key: 'monthly', label: 'Every month', frequency: 'monthly', interval: 1 },
  { key: 'custom', label: 'Custom' },
]

const END_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: 'on_date', label: 'On a date' },
  { value: 'after_count', label: 'After a number of times' },
]

// Minutes before the occurrence's own due moment. Occurrences are due at
// 23:59 in the account's timezone (see docs/tasks.md, "Due dates are
// date-only in the UI"), so "1 day before" lands the evening before, which
// is what a reminder for a daily or weekly chore usually wants.
const REMIND_OPTIONS = [
  { value: '', label: 'No reminder' },
  { value: 0, label: 'At the due time' },
  { value: 60, label: '1 hour before' },
  { value: 180, label: '3 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 2880, label: '2 days before' },
  { value: 10080, label: '1 week before' },
]

function todayIso() {
  return new Date().toISOString().split('T')[0]
}

function defaultRule() {
  return {
    frequency: 'daily',
    interval: 1,
    starts_on: todayIso(),
    days_of_week: [],
    day_of_month: null,
    due_time: null,
    remind_before_minutes: null,
    ends: { type: 'never', date: null, count: null },
  }
}

const props = defineProps({
  modelValue: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue'])

const rule = reactive({
  ...defaultRule(),
  ...(props.modelValue || {}),
  ends: { ...defaultRule().ends, ...(props.modelValue?.ends || {}) },
})

watch(rule, (val) => emit('update:modelValue', JSON.parse(JSON.stringify(val))), { deep: true })

/* ------------------------------------------------------------------ *
 * Dates
 * ------------------------------------------------------------------ */

// 'YYYY-MM-DD' is a plain calendar date, so it is read back at midday UTC
// with the formatter pinned to UTC. Parsing it as a local instant and
// re-reading it in another zone is what shifts a date by a day (see
// useTaskDates.js).
function formatDate(dateStr) {
  if (!dateStr) return ''
  return formatDateInMask(`${dateStr}T12:00:00Z`, dateFormatMask(globalStore.user?.date_format), 'UTC')
}

function weekdayKeyOf(dateStr) {
  if (!dateStr) return 'mon'
  return DAY_KEYS[new Date(`${dateStr}T12:00:00Z`).getUTCDay()]
}

function dayOfMonthOf(dateStr) {
  if (!dateStr) return 1
  return new Date(`${dateStr}T12:00:00Z`).getUTCDate()
}

// A cleared start date is not a valid rule: the backend expander returns no
// dates at all when `starts_on` is blank, so the series would quietly
// generate nothing. Falling back to today keeps it always answerable.
const startsOnModel = computed({
  get: () => rule.starts_on || todayIso(),
  set: (value) => { rule.starts_on = value || todayIso() },
})

const endsOnModel = computed({
  get: () => rule.ends.date || '',
  set: (value) => { rule.ends.date = value || null },
})

/* ------------------------------------------------------------------ *
 * Presets
 * ------------------------------------------------------------------ */

function sortedDays(keys) {
  return [...keys].sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b))
}

// Mirrors the backend: with no days chosen, a weekly rule falls back to the
// start date's own weekday (Tasks::RecurrenceExpander#weekly_dates). The UI
// has to agree, or an empty day row would imply "no days, nothing happens".
const effectiveDays = computed(() =>
  rule.days_of_week.length ? sortedDays(rule.days_of_week) : [weekdayKeyOf(rule.starts_on)]
)

// Which preset the rule currently *looks* like. Note that the day row is
// not part of the match beyond the weekday case: adding Thursday to a weekly
// schedule is still "every week", and kicking the user out to Custom for it
// would pop the interval control open for no reason.
const derivedPreset = computed(() => {
  const days = sortedDays(rule.days_of_week)
  const sameDays = (other) => days.length === other.length && days.every((d, i) => d === other[i])

  if (rule.frequency === 'daily' && rule.interval === 1) return 'daily'
  if (rule.frequency === 'weekly' && rule.interval === 1 && sameDays(WEEKDAY_KEYS)) return 'weekdays'
  if (rule.frequency === 'weekly' && rule.interval === 1) return 'weekly'
  if (rule.frequency === 'weekly' && rule.interval === 2) return 'biweekly'
  if (rule.frequency === 'monthly' && rule.interval === 1) return 'monthly'
  return 'custom'
})

// Custom has to be a real mode, not something inferred. Deriving it alone
// meant clicking Custom set an interval that then matched another preset,
// and the highlight jumped straight back off Custom to that chip.
const customMode = ref(derivedPreset.value === 'custom')

const activePreset = computed(() => (customMode.value ? 'custom' : derivedPreset.value))

// An edit that no longer resembles any preset is genuinely custom, so keep
// the mode honest when the rule is changed through the controls rather than
// the chips.
watch(derivedPreset, (value) => {
  if (value === 'custom') customMode.value = true
})

function applyPreset(preset) {
  if (preset.key === 'custom') {
    customMode.value = true
    // Land on something concrete rather than an empty custom form.
    rule.frequency = rule.frequency || 'weekly'
    rule.interval = Math.max(2, Number(rule.interval) || 2)
    return
  }

  customMode.value = false
  rule.frequency = preset.frequency
  rule.interval = preset.interval

  if (preset.frequency === 'weekly') {
    rule.days_of_week = preset.days === 'start' ? [weekdayKeyOf(rule.starts_on)] : [...preset.days]
  } else {
    rule.days_of_week = []
  }

  if (preset.frequency === 'monthly' && rule.day_of_month == null) {
    rule.day_of_month = dayOfMonthOf(rule.starts_on)
  }
}

// Deselecting the last day is refused rather than silently falling back to
// the start weekday: an all empty row that still generates occurrences is a
// worse surprise than a button that will not turn off.
function toggleDay(key) {
  const index = rule.days_of_week.indexOf(key)
  if (index === -1) rule.days_of_week.push(key)
  else if (rule.days_of_week.length > 1) rule.days_of_week.splice(index, 1)
}

// Switching to weekly through the Custom control leaves the day row empty,
// which would read as "no days selected". Seed it from the start date, the
// same day the backend would have picked anyway.
watch(() => rule.frequency, (frequency) => {
  if (frequency === 'weekly' && !rule.days_of_week.length) {
    rule.days_of_week = [weekdayKeyOf(rule.starts_on)]
  }
  if (frequency === 'monthly' && rule.day_of_month == null) {
    rule.day_of_month = dayOfMonthOf(rule.starts_on)
  }
})

const dayOfMonthModel = computed({
  get: () => (rule.day_of_month != null ? String(rule.day_of_month) : String(dayOfMonthOf(rule.starts_on))),
  set: (value) => { rule.day_of_month = value === 'last' ? 'last' : Number(value) },
})

const shortMonthWarning = computed(
  () => rule.frequency === 'monthly' && Number(dayOfMonthModel.value) > 28
)

const remindBeforeModel = computed({
  get: () => (rule.remind_before_minutes == null ? '' : String(rule.remind_before_minutes)),
  set: (value) => { rule.remind_before_minutes = value === '' ? null : Number(value) },
})

/* ------------------------------------------------------------------ *
 * Plain language summary
 * ------------------------------------------------------------------ */

function joinWords(list) {
  if (list.length <= 1) return list[0] || ''
  if (list.length === 2) return `${list[0]} and ${list[1]}`
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`
}

const summary = computed(() => {
  const every = Number(rule.interval) > 1 ? `Every ${rule.interval}` : 'Every'
  let cadence

  if (rule.frequency === 'daily') {
    cadence = Number(rule.interval) > 1 ? `${every} days` : 'Every day'
  } else if (rule.frequency === 'weekly') {
    const names = effectiveDays.value.map((key) => DAYS.find((d) => d.key === key)?.name).filter(Boolean)
    const unit = Number(rule.interval) > 1 ? `${every} weeks` : 'Every week'
    cadence = `${unit} on ${joinWords(names)}`
  } else {
    const unit = Number(rule.interval) > 1 ? `${every} months` : 'Every month'
    cadence = dayOfMonthModel.value === 'last'
      ? `${unit} on the last day`
      : `${unit} on day ${dayOfMonthModel.value}`
  }

  const parts = [cadence]
  const start = formatDate(startsOnModel.value)
  if (start) parts.push(`starting ${start}`)

  if (rule.ends.type === 'on_date' && rule.ends.date) {
    parts.push(`until ${formatDate(rule.ends.date)}`)
  } else if (rule.ends.type === 'after_count' && Number(rule.ends.count) > 0) {
    parts.push(`${rule.ends.count} times in total`)
  }

  return `${parts.join(', ')}.`
})
</script>
